import React from 'react'

import { Button, Icon, palette } from '@centivo/react-components'
import { useHistory, useLocation } from 'react-router-dom'
import { Styles } from 'react-modal'

import { useMediaContext } from 'src/context/responsive'
import { SearchResultsCounter } from 'src/refactor/components'
import { NoResultsErrorMessage } from 'src/refactor/components/NoResultsErrorMessage/NoResultsErrorMessage'
import { useToggle } from 'src/hooks'
import { Modal } from 'src/refactor/blocks/Modal'
import EnhancedFilter from 'src/views/directory/components/EnhancedFilter'

import IconFilter from 'src/assets/icons/filter/IconFilter'
import { useResultsState } from 'src/context/results'
import { GetProvidersFilter } from 'src/api/types'
import styled from 'styled-components'
import { TSearchType } from 'src/shared/types'
import { unique } from 'src/refactor/helpers/array'
import { useGlobalState } from 'src/context/global'
import { FilterButton } from './FilterButton'
import { RlhContainer } from './ResultsListHeader.styled'
import { TabFilter } from './TabFilter'

const ViewButton = styled(Button)`
  color: ${palette.ui.blue};
  margin-left: auto;

  ${({ theme }) => {
    switch (theme.template) {
      case 'template-4':
        return {
          fontWeight: 500,
          fontSize: '0.875rem',
        }
      default:
        return {
          fontWeight: 600,
          fontSize: '0.75rem',
        }
    }
  }}
`

const getModalStyle = (isMobileView: boolean): Styles =>
  isMobileView
    ? {
        content: {
          height: `${window.innerHeight}px`,
          width: '100%',
          zIndex: 100,
          margin: 0,
          inset: 0,
        },
      }
    : {
        content: {
          padding: '0',
          width: 'max-content',
          minWidth: '40rem',
          backgroundColor: 'transparent',
        },
      }

const sliceTabFilterOptions: Readonly<
  {
    label: string
    value: 'all' | TSearchType
  }[]
> = [
  { label: 'View All', value: 'all' },
  { label: 'Doctors', value: 'practitioner' },
  { label: 'Practices', value: 'practice' },
] as const

export const ResultsListHeader: React.FC<{
  hideText: boolean
  setCurrentPage(page: number): void
  isListEmpty?: boolean
  shouldHideMap?: boolean
  isRequestCanceled?: boolean
}> = ({
  hideText,
  setCurrentPage,
  isListEmpty,
  shouldHideMap = false,
  isRequestCanceled,
}) => {
  const history = useHistory()
  const { isMobileView } = useMediaContext()
  const { search } = useLocation()
  const [isModalOpen, toggleModal, setModalOpen] = useToggle(false)
  const [state, actions] = useResultsState()

  const { unfilteredItems = [] } = state

  const itemTypes = unique(unfilteredItems?.map(({ itemType }) => itemType))
  const [{ appConfig }] = useGlobalState()
  const isSliceTemplate = appConfig.template === 'template-4'

  const shouldShowTypeFilters =
    state.itemType === 'practitioner' && itemTypes.length > 1

  const onRequestClose = () => setModalOpen(false)

  const onToggleView = () => {
    const query = new URLSearchParams(search)
    const view = actions.toggleListView()

    query.delete('view')
    query.append('view', view)

    history.replace({
      search: query.toString(),
    })
  }

  const modalStyle = getModalStyle(isMobileView)

  const setFilterOptions = (filter: GetProvidersFilter) => {
    actions?.search({
      filter,
    })

    setCurrentPage(0)
  }
  const onSelectType = (
    providerType: 'all' | Exclude<TSearchType, 'facility'>
  ) =>
    actions.filter({
      providerType,
    })

  return (
    <RlhContainer isEmpty={isListEmpty} isCanceled={isRequestCanceled}>
      {!hideText && (!isListEmpty || isRequestCanceled) && (
        <SearchResultsCounter />
      )}
      {shouldShowTypeFilters && (
        <TabFilter
          options={sliceTabFilterOptions}
          value={state.filter.providerType}
          onSelect={onSelectType}
          defaultValue="all"
        />
      )}
      <Modal
        style={modalStyle}
        isOpen={isModalOpen}
        onRequestClose={onRequestClose}
      >
        <EnhancedFilter
          onSubmit={setFilterOptions}
          onRequestClose={onRequestClose}
          initialValues={state.filter}
          displayType={state.itemType}
          specialtyOptions={state.specialtyFilters}
          shouldShowSpecialtyFilters={!!state.search.serviceLines}
          shouldShowVirtualCareAvailable={state.isVirtualCareAvailable}
        />
      </Modal>
      {!isListEmpty || isRequestCanceled ? (
        <FilterButton onClick={toggleModal}>
          Filters
          <Icon size="1.5rem" icon="filter-list" aria-hidden />
        </FilterButton>
      ) : null}
      {!shouldHideMap && isMobileView && !isListEmpty && (
        <ViewButton buttonType="link" onClick={onToggleView}>
          {state.listView === 'map' ? 'List View' : 'Map View'}
        </ViewButton>
      )}
      {isRequestCanceled && (
        <p>
          Try changing your search criteria or use the filter to expand your
          search radius.
        </p>
      )}
      {isListEmpty && !isRequestCanceled && <NoResultsErrorMessage />}
    </RlhContainer>
  )
}
